export type RouteMatchMode = "exact" | "prefix";

export interface RouteDefinition<TMetadata = unknown> {
  readonly path: string;
  readonly match?: RouteMatchMode;
  readonly metadata: TMetadata;
}

export interface RouteLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export interface RouteSnapshot<TMetadata> {
  readonly location: RouteLocation;
  readonly route: RouteDefinition<TMetadata>;
  readonly found: boolean;
  readonly params: Readonly<Record<string, string>>;
}

export interface BrowserHistoryLike {
  pushState(data: unknown, unused: string, url?: string | URL | null): void;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export interface BrowserWindowLike {
  readonly location: RouteLocation & { readonly origin: string };
  readonly history: BrowserHistoryLike;
  addEventListener(type: "popstate", listener: () => void): void;
  removeEventListener(type: "popstate", listener: () => void): void;
}

export interface BrowserRouter<TMetadata> {
  getSnapshot(): RouteSnapshot<TMetadata>;
  subscribe(listener: () => void): () => void;
  blockNavigation(guard: NavigationGuard): () => void;
  navigate(to: string, options?: { readonly replace?: boolean }): boolean;
}

export type NavigationGuard = (to: string, options: {
  readonly source: "programmatic" | "history";
  readonly replace: boolean;
}) => boolean;

function normalizePathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const collapsed = withLeadingSlash.replace(/\/{2,}/g, "/");
  return collapsed.length > 1 ? collapsed.replace(/\/$/, "") : collapsed;
}

function matchParameters(
  pathname: string,
  route: RouteDefinition,
): Readonly<Record<string, string>> | null {
  const routePath = normalizePathname(route.path);
  const pathnameSegments = pathname === "/" ? [] : pathname.slice(1).split("/");
  const routeSegments = routePath === "/" ? [] : routePath.slice(1).split("/");
  const exact = (route.match ?? "exact") === "exact";
  if ((exact && pathnameSegments.length !== routeSegments.length)
    || (!exact && pathnameSegments.length < routeSegments.length)) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let index = 0; index < routeSegments.length; index += 1) {
    const expected = routeSegments[index];
    const actual = pathnameSegments[index];
    if (!expected || !actual) return null;
    if (expected.startsWith(":")) params[expected.slice(1)] = decodeURIComponent(actual);
    else if (expected !== actual) return null;
  }
  return params;
}

export function resolveRoute<TMetadata>(
  pathname: string,
  routes: readonly RouteDefinition<TMetadata>[],
  fallback: RouteDefinition<TMetadata>,
): { route: RouteDefinition<TMetadata>; found: boolean; params: Readonly<Record<string, string>> } {
  const normalized = normalizePathname(pathname);
  for (const route of routes) {
    const params = matchParameters(normalized, route);
    if (params) return { route, found: true, params };
  }
  return { route: fallback, found: false, params: {} };
}

export function createBrowserRouter<TMetadata>(options: {
  readonly window: BrowserWindowLike;
  readonly routes: readonly RouteDefinition<TMetadata>[];
  readonly fallback: RouteDefinition<TMetadata>;
}): BrowserRouter<TMetadata> {
  const listeners = new Set<() => void>();
  const guards = new Set<NavigationGuard>();

  const readSnapshot = (): RouteSnapshot<TMetadata> => {
    const location = {
      pathname: normalizePathname(options.window.location.pathname),
      search: options.window.location.search,
      hash: options.window.location.hash,
    };
    const resolved = resolveRoute(location.pathname, options.routes, options.fallback);
    return { location, ...resolved };
  };

  let snapshot = readSnapshot();
  const targetOf = (location: RouteLocation) => `${location.pathname}${location.search}${location.hash}`;
  const allowed = (to: string, source: "programmatic" | "history", replace: boolean) => {
    for (const guard of guards) if (!guard(to, { source, replace })) return false;
    return true;
  };
  const update = () => {
    snapshot = readSnapshot();
    for (const listener of listeners) listener();
  };
  const onPopState = () => {
    const target = targetOf(readSnapshot().location);
    if (!allowed(target, "history", true)) {
      // History has already moved; restore the current screen before notifying React.
      options.window.history.pushState(null, "", targetOf(snapshot.location));
      return;
    }
    update();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      if (listeners.size === 0) options.window.addEventListener("popstate", onPopState);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) options.window.removeEventListener("popstate", onPopState);
      };
    },
    blockNavigation(guard) {
      guards.add(guard);
      return () => { guards.delete(guard); };
    },
    navigate(to, navigateOptions) {
      const url = new URL(to, options.window.location.origin);
      if (url.origin !== options.window.location.origin) {
        throw new Error("CarePass router navigation must remain on the current origin.");
      }
      const target = `${normalizePathname(url.pathname)}${url.search}${url.hash}`;
      if (!allowed(target, "programmatic", navigateOptions?.replace ?? false)) return false;
      if (navigateOptions?.replace) options.window.history.replaceState(null, "", target);
      else options.window.history.pushState(null, "", target);
      update();
      return true;
    },
  };
}
